import { Link } from "react-router-dom";
import { ChevronRight, ArrowUpRight } from "lucide-react";

import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import Jar from "@/components/visual/Jar";
import { BRAND, LAB, LAB_REPORT, PURITY_REPORT } from "@/content/brand";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const SITE_URL =
  process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const PATH = "/lab-reports";
const SEO_TITLE = "Lab Reports: Every Batch Tested";
const SEO_DESCRIPTION =
  "Karijeeva lab reports: purity results against the FSSAI coconut oil standard, nutrition per 100 g, who tested the oil, and how to get your own batch report.";

const SUPPORT_EMAIL = "support@karijeeva.in";
const REPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Lab report for my batch")}&body=${encodeURIComponent(
  "Hello Karijeeva,\n\nPlease send me the full lab report for batch number: \n\nThank you."
)}`;

const val = (param) => LAB_REPORT.rows.find((r) => r.param === param) || { value: "", unit: "" };


function SectionHead({ eyebrow, title, sub, dark = false, center = true }) {
  return (
    <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
      {eyebrow && <p className={`font-body text-[15px] font-semibold ${dark ? "text-brand-bone/70" : "text-brand-gold"}`}>{eyebrow}</p>}
      <h2 className={`font-display mt-2 text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.02] tracking-display ${dark ? "text-white" : "text-brand-ink"}`}>
        {title}
      </h2>
      {sub && <p className={`mt-5 font-body text-[17px] sm:text-[19px] leading-relaxed ${dark ? "text-brand-bone/70" : "text-ink-muted"}`}>{sub}</p>}
    </div>
  );
}

function ExternalLink({ href, children, dark = false }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 font-body text-[15px] hover:underline ${dark ? "text-white" : "text-brand-gold"}`}
    >
      {children} <ArrowUpRight className="w-4 h-4" />
    </a>
  );
}

function Hero() {
  return (
    <section className="bg-brand-obsidian text-brand-bone overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
        <div>
          <nav aria-label="Breadcrumb" className="font-body text-[13px] text-brand-bone/60">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span>Lab reports</span>
          </nav>
          <p className="mt-6 font-body text-[15px] sm:text-[17px] font-semibold text-brand-bone/80">Lab reports</p>
          <h1 className="font-display text-white mt-3 text-[clamp(2.8rem,8vw,6rem)] leading-[0.98] tracking-display">
            Tested.
            <br />
            Every batch.
          </h1>
          <p className="mt-6 max-w-xl font-body text-[17px] sm:text-[21px] leading-snug text-brand-bone/75">
            Before any batch of Karijeeva is packed, a sample goes to an independent food testing lab. We have done this for {BRAND.batchesTested} batches so far.
            Below is our latest report, and what each number means.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
            <a href="#latest-report" className="inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[15px] px-6 py-3 transition-colors">
              Read the report
            </a>
            <a href={REPORT_MAILTO} className="inline-flex items-center gap-1 font-body text-[15px] text-brand-bone hover:underline">
              Ask for your batch report <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="mx-auto w-[180px] sm:w-[220px]">
          <Jar className="w-full h-auto" />
        </div>
      </div>
    </section>
  );
}

function ReportTable() {
  return (
    <section id="latest-report" className="bg-brand-parchment py-24 sm:py-32 scroll-mt-16" data-testid="lab-report-table">
      <div className="max-w-4xl mx-auto px-6">
        <SectionHead
          eyebrow="Our latest report"
          title="What the lab found."
          sub="These are the nutrition results from our most recent report, exactly as the lab gave them. All values are per 100 g of oil."
        />
        <div className="mt-14 rounded-3xl bg-white p-6 sm:p-10">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="font-body text-[14px] text-ink-muted">
              Report reference <span className="font-semibold text-brand-ink">{LAB_REPORT.ref}</span>
            </p>
            <p className="font-body text-[14px] text-ink-muted">Values per 100 g</p>
          </div>
          <dl className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2 font-body text-[14px]" data-testid="lab-sample-details">
            {[
              ["Laboratory", `${LAB.name}, ${LAB.city} (${LAB.credential})`],
              ["Sample", LAB_REPORT.sample],
              ["Received by the lab", fmtDate(LAB_REPORT.received)],
              ["Analysed", LAB_REPORT.analysed],
              ["Report issued", fmtDate(LAB_REPORT.issued)],
              ["Signed by", "The lab's analyst and authorised signatory"],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-2"><dt className="text-ink-muted shrink-0">{k}:</dt><dd className="text-brand-ink">{v}</dd></div>
            ))}
          </dl>
          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[520px] text-left font-body">
              <caption className="sr-only">Karijeeva lab report {LAB_REPORT.ref}, nutrition per 100 g</caption>
              <thead>
                <tr className="border-b border-black/10">
                  <th scope="col" className="py-3 pr-4 font-normal text-[13px] text-ink-muted">Parameter</th>
                  <th scope="col" className="py-3 pr-4 font-normal text-[13px] text-ink-muted text-right">Result</th>
                  <th scope="col" className="py-3 pr-4 font-normal text-[13px] text-ink-muted">Unit</th>
                  <th scope="col" className="py-3 font-normal text-[13px] text-ink-muted">Test method</th>
                </tr>
              </thead>
              <tbody>
                {LAB_REPORT.rows.map((r) => (
                  <tr key={r.param} className="border-b border-black/10 last:border-0">
                    <th scope="row" className="py-4 pr-4 font-normal text-[16px] text-brand-ink">{r.param}</th>
                    <td className="py-4 pr-4 font-display text-[22px] text-brand-ink text-right tabular-nums">{r.value}</td>
                    <td className="py-4 pr-4 text-[15px] text-ink-muted">{r.unit}</td>
                    <td className="py-4 text-[15px] text-ink-muted">{r.method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 font-body text-[14px] leading-relaxed text-ink-muted">
            AOAC and IS are published standard methods. The FSSAI manual refers to the FSSAI Manual of Methods of Analysis. In house means the lab's own method.
          </p>
        </div>
      </div>
    </section>
  );
}

function Meaning() {
  const sat = val("Saturated fat");
  const fat = val("Total fat");
  const energy = val("Energy");
  const chol = val("Cholesterol");
  const items = [
    {
      title: "Saturated fat",
      big: `${sat.value} ${sat.unit}`,
      body: `Of the ${fat.value} g of fat in 100 g, ${sat.value} g is saturated. That is normal for coconut oil, and it is why the oil sets white on a cool morning. It also means coconut oil is best used as one of the oils in your kitchen, not the only one.`,
      link: { href: "https://www.nin.res.in/faqs.html", label: "ICMR NIN on using a combination of oils" },
    },
    {
      title: "Energy",
      big: `${energy.value} ${energy.unit}`,
      body: "Every pure fat gives about 9 kcal per gram, whether it is coconut oil, groundnut oil or ghee. So this number is about the same for any cooking oil. A spoonful goes a long way.",
    },
    {
      title: "Cholesterol",
      big: `${chol.value} ${chol.unit}`,
      body: "Cholesterol is made only by animals, so no plant oil contains it. Our result is what you would expect from any coconut oil. We show it because it is on the report, not because it makes our oil special.",
    },
    {
      title: "Carbohydrates, protein, fibre",
      big: "Traces",
      body: "Oil is almost entirely fat. The amounts of carbohydrate and protein the lab found are tiny traces, as you would expect in an oil.",
    },
  ];
  return (
    <section className="bg-brand-parchment-soft py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHead
          eyebrow="In plain words"
          title="What do these numbers mean?"
          sub="A lab report is only useful if you can read it. Here is what matters, told straight."
        />
        <div className="mt-14 grid md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={it.title} className="rounded-3xl bg-white p-8 sm:p-10 flex flex-col">
              <p className="font-body text-[13px] font-semibold text-brand-gold">{it.title}</p>
              <p className="font-display text-[44px] leading-none tracking-display text-brand-ink mt-3">{it.big}</p>
              <p className="font-body text-[16px] leading-relaxed text-ink-muted mt-5">{it.body}</p>
              {it.link && (
                <p className="mt-auto pt-5">
                  <ExternalLink href={it.link.href}>{it.link.label}</ExternalLink>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Purity() {
  return (
    <section id="purity" className="bg-brand-obsidian text-brand-bone py-24 sm:py-32 scroll-mt-16" data-testid="lab-purity">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHead
          dark
          eyebrow="Purity"
          title="Meets the FSSAI standard for coconut oil. On every test."
          sub="Nutrition tells you what is in the oil. Purity tests tell you whether anything else is. The same sample was checked against the FSSAI standard for coconut oil, and the lab confirmed it conforms."
        />
        <div className="mt-14 overflow-x-auto rounded-3xl bg-brand-obsidian-soft p-6 sm:p-8">
          <p className="font-body text-[13px] text-brand-bone/60">
            Report {PURITY_REPORT.ref}, issued {new Date(PURITY_REPORT.issued).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}. Standard: {PURITY_REPORT.standard}.
          </p>
          <table className="mt-4 w-full min-w-[640px] text-left font-body">
            <thead>
              <tr className="border-b border-white/10 text-[13px] text-brand-bone/60">
                <th scope="col" className="py-3 pr-4 font-normal">Test</th>
                <th scope="col" className="py-3 pr-4 font-normal">Our result</th>
                <th scope="col" className="py-3 pr-4 font-normal">FSSAI limit</th>
                <th scope="col" className="py-3 font-normal">What it tells you</th>
              </tr>
            </thead>
            <tbody>
              {PURITY_REPORT.rows.map((r) => (
                <tr key={r.param} className="border-b border-white/10 align-top">
                  <th scope="row" className="py-4 pr-4 font-normal text-white">{r.param}</th>
                  <td className="py-4 pr-4 font-display text-[20px] text-white whitespace-nowrap">{r.value}</td>
                  <td className="py-4 pr-4 text-brand-bone/75 whitespace-nowrap">{r.limit}</td>
                  <td className="py-4 text-[15px] text-brand-bone/75 leading-relaxed">{r.plain}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 max-w-3xl font-body text-[15px] leading-relaxed text-brand-bone/70">
          These are the tests the FSSAI standard uses to check that coconut oil is what it says it is. Mixing in meaningful amounts of seed oils or mineral oil shifts these numbers. No single test catches every kind of mixing, which is why the standard uses several together, and why we test every batch rather than once.
        </p>
        <div className="mt-10 rounded-3xl bg-brand-obsidian-soft p-8 sm:p-10 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center">
          <div>
            <p className="font-display text-white text-[26px] sm:text-[30px] leading-tight tracking-heading">Ask us for the report for your batch.</p>
            <p className="mt-3 font-body text-[16px] leading-relaxed text-brand-bone/75">
              Every jar carries a batch number on the label. Write to us with that number and we will send you the lab report for the batch in your kitchen.
            </p>
          </div>
          <div className="flex flex-col items-start gap-3">
            <a href={REPORT_MAILTO} className="inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[15px] px-6 py-3 transition-colors" data-testid="lab-request-report">
              Email {SUPPORT_EMAIL}
            </a>
            <Link to="/coconut-oil-lab-report-explained" className="inline-flex items-center gap-1 font-body text-[15px] text-white hover:underline">
              Every test explained, with science and Ayurveda <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/how-to-check-coconut-oil-purity" className="inline-flex items-center gap-1 font-body text-[15px] text-white hover:underline">
              Simple purity checks you can do at home <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Verify() {
  return (
    <section className="bg-brand-parchment py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHead
          eyebrow="Don't just take our word"
          title="Check us yourself."
          sub="You can confirm our licence on a government register, and see exactly who tested the oil."
        />
        <div className="mt-14 grid md:grid-cols-2 gap-4">
          <div className="rounded-3xl bg-white p-8 sm:p-10 flex flex-col">
            <p className="font-body text-[13px] font-semibold text-brand-gold">FSSAI licence</p>
            <p className="font-display text-[34px] sm:text-[40px] leading-none tracking-heading text-brand-ink mt-3 break-all">{BRAND.fssai}</p>
            <p className="font-body text-[16px] leading-relaxed text-ink-muted mt-5">
              Karijeeva is made by {BRAND.company} under this FSSAI licence. Open FoSCoS, the FSSAI licensing portal, use its licence search, and enter the number above to see who holds it.
            </p>
            <p className="mt-auto pt-6">
              <ExternalLink href="https://foscos.fssai.gov.in/">Search on FSSAI FoSCoS</ExternalLink>
            </p>
          </div>
          <div className="rounded-3xl bg-white p-8 sm:p-10 flex flex-col">
            <p className="font-body text-[13px] font-semibold text-brand-gold">The lab</p>
            <p className="font-display text-[34px] sm:text-[40px] leading-none tracking-heading text-brand-ink mt-3">{LAB.name}</p>
            <p className="font-body text-[16px] leading-relaxed text-ink-muted mt-5">
              An independent food testing laboratory in {LAB.city}, {LAB.credential}. We send the samples; the lab runs the tests and signs the report. Its name, address and signatures are on every report we share.
            </p>
            <p className="mt-auto pt-6">
              <ExternalLink href={LAB.url}>Visit {LAB.name}</ExternalLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Next() {
  const links = [
    { to: "/the-farm", label: "Where our coconuts grow" },
    { to: "/cold-press-process", label: "How we make it" },
    { to: "/cold-pressed-vs-refined-coconut-oil", label: "Cold pressed vs refined" },
    { to: "/faqs", label: "Questions people ask" },
  ];
  return (
    <section className="bg-brand-obsidian text-center py-28 sm:py-36">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="font-display text-white text-[clamp(2.4rem,6vw,4.5rem)] leading-[1] tracking-display">
          Coconut oil you can check.
        </h2>
        <p className="mt-6 font-body text-[19px] text-brand-bone/70">One ingredient, from our family farms, tested before it is packed.</p>
        <Link to="/products" className="mt-10 inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[17px] px-7 py-3.5 transition-colors">
          Shop Karijeeva
        </Link>
        <ul className="mt-12 flex flex-wrap justify-center gap-3">
          {links.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="inline-flex rounded-control bg-brand-obsidian-soft px-4 py-2 font-body text-[14px] text-brand-bone hover:text-white">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function LabReports() {
  const url = `${SITE_URL}${PATH}`;
  const webPageLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Karijeeva lab reports",
    description: SEO_DESCRIPTION,
    url,
    dateModified: PURITY_REPORT.issued,
    publisher: { "@type": "Organization", name: BRAND.company, brand: BRAND.name, url: SITE_URL },
    about: { "@type": "Product", name: "Karijeeva Cold Pressed Coconut Oil", brand: { "@type": "Brand", name: BRAND.name } },
  };

  return (
    <>
      <Seo
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url={url}
        jsonLd={[webPageLd, breadcrumbLd([{ name: "Home", path: "/" }, { name: "Lab reports", path: PATH }])]}
      />
      <div data-testid="page-lab-reports">
        <Hero />
        <ReportTable />
        <Meaning />
        <Purity />
        <Verify />
        <Next />
      </div>
    </>
  );
}
